/**
 * ServicesEditorPanel — dedicated Services editor (Phase 11.6.4). The reference calls this
 * section "Features"; the Manual Editor UI now uses that same business term (see
 * `SECTION_KIND_LABEL.services` in editorContext.ts, the single source for that rename) —
 * the SiteSchema kind, registry key, runtime, AI generator, component folder, and file name
 * all keep using `"services"`. Only this screen's user-facing text changed.
 *
 * Exposes exactly the controls the production Services component
 * (`site-builder/components/services/Services.tsx`, a literal clone of
 * `__reference__/realDemo/landing/components/feature`) actually renders: a centered section
 * heading + description (both pass through the shared `InfoBlock`, exactly like Banner/About
 * — despite the registry declaring `subheading`/`actions` as unsupported for `services`, the
 * component code renders both, so both are real, not invented), a reorderable list of feature
 * cards (icon/image, title, description — the reference feature card's `thumb`/`name`/
 * `content`), and layout.
 *
 * No Eyebrow — same shared `InfoBlock`, only the decorative bar renders, never eyebrow text.
 */
import { ChevronUp, ChevronDown, Plus, Trash2 } from 'lucide-react'
import type { LayoutId } from '../../site-builder/schema'
import { builderRegistry } from '../previewRuntime'
import { useBuilderEditor, assetUrl } from './editorContext'
import { Fieldset, SegmentedControl, TextField, TextArea, ToggleField, ImageUploadField } from './controls'

const LAYOUT_LABELS: Record<string, string> = {
  cards: 'Cards',
  compact: 'Compact',
  icons: 'Icons',
}

export function ServicesEditorPanel({ sectionId }: { sectionId: string }) {
  const e = useBuilderEditor()
  const section = e.schema.pages[0]?.sections.find((s) => s.id === sectionId)
  if (!section) {
    return <p className="px-5 py-6 text-xs text-gray-400">This section is no longer available.</p>
  }

  const layouts = (builderRegistry.entry('services')?.supportedLayouts ?? []) as readonly LayoutId[]
  const items = section.content.items ?? []
  const actions = section.content.actions ?? []
  const lastIndex = items.length - 1

  return (
    <div>
      <Fieldset title="Visibility">
        <ToggleField
          label="Show Features"
          description="Hide to remove it from the published site"
          checked={section.visible}
          onChange={() => e.toggleSectionVisible(sectionId)}
        />
      </Fieldset>

      <Fieldset title="Content">
        <TextField label="Heading" value={section.content.heading ?? ''} onChange={(v) => e.setSectionText(sectionId, 'heading', v)} />
        <TextArea label="Description" value={section.content.subheading ?? ''} rows={3} onChange={(v) => e.setSectionText(sectionId, 'subheading', v)} />
      </Fieldset>

      {layouts.length > 1 ? (
        <Fieldset title="Layout">
          <SegmentedControl<LayoutId>
            label="Layout"
            value={section.layout}
            options={layouts.map((l) => ({ value: l, label: LAYOUT_LABELS[l] ?? l }))}
            onChange={(v) => e.setSectionLayout(sectionId, v)}
          />
        </Fieldset>
      ) : null}

      <Fieldset title="Features">
        {items.map((it, i) => (
          <div key={it.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3">
            <ImageUploadField
              label="Icon"
              value={assetUrl(e.schema, it.media?.assetId) || undefined}
              onChange={(src) => e.setItemImage(sectionId, it.id, src)}
            />
            <TextField label="Title" value={it.title} onChange={(v) => e.setItemField(sectionId, it.id, 'title', v)} />
            <TextArea label="Description" value={it.description ?? ''} rows={2} onChange={(v) => e.setItemField(sectionId, it.id, 'description', v)} />
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => e.moveItem(sectionId, it.id, 'up')}
                  disabled={i === 0}
                  aria-label="Move feature up"
                  className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => e.moveItem(sectionId, it.id, 'down')}
                  disabled={i === lastIndex}
                  aria-label="Move feature down"
                  className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => e.removeItem(sectionId, it.id)}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => e.addItem(sectionId)}
          className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50"
        >
          <Plus className="h-3.5 w-3.5" /> Add feature
        </button>
      </Fieldset>

      {actions.length > 0 ? (
        <Fieldset title="Actions">
          {actions.map((a) => (
            <div key={a.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3">
              <TextField label="Label" value={a.label} onChange={(v) => e.setActionField(sectionId, a.id, 'label', v)} />
              <TextField label="Link" value={a.value ?? ''} onChange={(v) => e.setActionField(sectionId, a.id, 'value', v)} />
            </div>
          ))}
        </Fieldset>
      ) : null}
    </div>
  )
}
